import type RpcMeta from "@/core/services/Rpc/RpcMeta";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import type { RequestOptions } from "@/core/services/Rpc/RpcService";
import Core from "@/core/Core";
import Tender from "@/domains/Tenders/Tender";
import TendersDAO from "@/domains/Tenders/TendersDAO";
import TenderPricePoint from "@/domains/Tenders/TenderPricePoint";
import WinnersCategory from "@/domains/Tenders/WinnersCategory";
import Dictionary from "@/core/types/Dictionary";
import Utils from "@/core/utils/Utils";
import type Store from "@/domains/Stores/Store";
import TimeZone from "@/domains/TimeZones/TimeZone";
import ParticipationDetailProduct
  from "@/domains/ParticipationDetailProduct/ParticipationDetailProduct";
import Demand from "@/domains/Demand/Demand";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import type OrderPosition from "@/domains/Orders/OrderPosition";
import type { UnwrapNestedRefs } from "vue";
import TenderParticipationRequest from "@/domains/TenderParticipationRequest/TenderParticipationRequest";
import BookmarksService from "@/domains/Bookmark/BookmarksService";
import DemandsService from "@/domains/Demand/DemandsService";
import TenderBidsService from "@/domains/TenderBids/TenderBidsService";
import TenderParticipationRequestsService
  from "@/domains/TenderParticipationRequest/TenderParticipationRequestsService";

class TendersService {
  static config = {
    /** Актуально при создании тендера/тендеров. Флаг говорит о паузе перед началом торгов
     * Это начальное состояние **/
    IS_CREATION_DELAY_MODE: true,
    /** Длина паузы перед началом тендера/тендеров **/
    CREATION_DELAY_MS: 20 * 60 * 1000, // 20 минут
    /** В этой переменной хранится частота обновления тендера в карточке и в реестре **/
    SOFT_POLL_INTERVAL: 5_000,  // 5 секунд
    HARD_POLL_INTERVAL: 60_000, // 60 секунд
  }

  /** Если пользователь не может что-то сделать, то тут хранится расшифровка **/
  static readonly UNAVAILABILITY_REASONS = {
    IS_SELLER: 'Продавец не может совершить это действие',
    IS_BUYER: 'Покупатель не может совершить это действие',
    NO_EXIST_ENTITY_ON_BACKEND: 'Тендер ещё не создан',
    YOU_ARE_PARTICIPATING: 'Вы уже сделали ставку',
    NO_EXIST_BID: 'Вы еще не сделали ставку',
    IS_NOT_FINISHING: 'Торги еще не окончены',
    IS_NOT_STARTED: 'Торги еще не начались',
    IS_NOT_RUNNING: 'Торги не в активной фазе',
    IS_FINISHED: 'Торги окончены',
    IS_DELETED: 'Тендер удалён',
    ORDERS_IS_NONE: 'Заказа нет',
    HAS_ORDER: 'Создан заказ',
    IS_NOT_APPROVED_SELLER: 'Вы не можете сделать ставку',
    TENDERS_WAS_EXHAUST: 'Тендер уже удовлетворён',
    OFFER_WAS_EXHAUST: 'Предложение исчерпано',
    IS_REFUSED: 'Вы отказались участвовать',
    IS_EVERYONE_CAN_BID: 'К этому тендеру нельзя указать причину неучастия',
    PERMISSION_NOT_NEEDED: 'К этому тендеру не требуется разрешение на участие',
    ALREADY_HAS_PERMISSION: 'У вас уже есть разрешение на участие',
    ALREADY_SENT_REQUEST: 'Вы уже отправили заявку на участие',
    PERMISSION_DECLINED: 'Ваша заявка на участие отклонена',
    IS_NOT_REFUSED: 'Вы не отказывались от участия',
    IS_EXCLUDED_FROM_TENDERS: 'Поставщик не допущен к торгам по этому разрешению',
    USE_TENDER_CARD: 'Действие доступно из карточки тендера',
  }

  /**
   * Статусы тендера для параметра status в tenders.getList.
   *
   * Статус приоритетнее флаговых фильтров: если status передан, флаги,
   * которые он перекрывает, бэком не учитываются. Значения не
   * взаимоисключающие — ACTUAL пересекается с RUNNING целиком
   * и с READY_FOR_ORDER частично (завершённые в пределах 24 часов).
   */
  static readonly STATUS = {
    NOT_STARTED: 'isNotStarted',
    ACTUAL: 'isActual',
    RUNNING: 'isRunning',
    READY_FOR_ORDER: 'isReadyForOrder',
    ORDER_CREATED: 'isOrderCreated',
  } as const;

  /**
   * Отображаемые названия статусов — общие для колонки «Статус» в реестре
   * (Tender.status) и для опций её фильтра. Один источник, чтобы фильтр
   * и содержимое колонки не разъезжались.
   *
   * ACTUAL названия не имеет: он не состояние тендера, а предикат выборки
   * для вкладки воронки, в колонке появиться не может.
   */
  static readonly STATUS_NAMES: Record<string, string> = {
    [TendersService.STATUS.NOT_STARTED]: 'Не начались',
    [TendersService.STATUS.RUNNING]: 'Идут торги',
    [TendersService.STATUS.READY_FOR_ORDER]: 'Ожидает заказа',
    [TendersService.STATUS.ORDER_CREATED]: 'Создан заказ',
  };

  /**
   * Опции фильтра статуса в колонке реестра.
   *
   * «Удалён» сюда не входит: удалённые отбираются флагом isDeleted
   * и живут на отдельной вкладке воронки.
   */
  static readonly STATUS_FILTER_OPTIONS = [
    TendersService.STATUS.NOT_STARTED,
    TendersService.STATUS.RUNNING,
    TendersService.STATUS.READY_FOR_ORDER,
    TendersService.STATUS.ORDER_CREATED,
  ].map(id => ({id, name: TendersService.STATUS_NAMES[id]}));

  /**
   * Схемы распределения объёма между победителями. Справочник фиксированный
   * и живёт на фронте: на бэке это значение в поле тендера, а не сущность.
   * Экземпляры создаются один раз — сравнение по ссылке остаётся рабочим,
   * и селект получает те же объекты, что лежат в модели.
   */
  static readonly WINNERS_CATEGORIES: Dictionary<WinnersCategory> = Dictionary.fromArray(
      [
        {id: 1, name: '1 победитель', value: '1 победитель'},
        {id: 2, name: '2 победителя (70/30)', value: '2 победителя (70/30)'},
        {
          id: 3,
          name: '1 победитель (не обязаны выкупить)',
          value: '1 победитель (не обязаны выкупить)'
        },
        {
          id: 4,
          name: '2 победителя (70/30) (не обязаны выкупить)',
          value: '2 победителя (70/30) (не обязаны выкупить)'
        },
        // айтем для фильтра
        {id: 0, name: 'Все', value: 'all'},
      ].map(item => WinnersCategory.fromServer(item)),
  );

  /** Категории, в которых потребность делится между двумя победителями в пропорции 70/30 */
  static readonly WINNERS_CATEGORY_SPLIT_70_30_IDS = [2, 4];

  /** Делится ли потребность между двумя победителями (70/30) */
  static isWinnersCategorySplit(winnersCategory: WinnersCategory | null | undefined): boolean {
    if (!winnersCategory) {
      return false;
    } else {
      return TendersService.WINNERS_CATEGORY_SPLIT_70_30_IDS.includes(winnersCategory.id);
    }
  }

  static showTenderCard(tender: Tender | null,
                        demand: Demand | null = null,
                        demands: Demand[] | null = null,
                        isFlexible: boolean | null = null): Promise<boolean> {
    return Core.dialogs.TenderCard.showAsPromise({
      id: tender?.id,
      demand: demand,
      demands: demands,
      isFlexible: isFlexible
    });
  }

  /** Карточка тендера по id — когда на руках облегчённая строка (например, график аналитики), а не модель тендера */
  static showTenderCardById(id: number): Promise<boolean> {
    return Core.dialogs.TenderCard.showAsPromise({ id });
  }

  /**
   * Заказы по тендеру: одна позиция — сразу её карточка, несколько — попап со списком.
   * target — якорь попапа (кнопка, по которой кликнули). Без якоря попап встаёт
   * на место контекстного меню, а вне меню — у курсора
   */
  static showOrders(tender: Tender | UnwrapNestedRefs<Tender>, target?: HTMLElement): Promise<boolean> {
    if (tender.orderPositions.length === 1) {
      return TendersService.showOrderPositionCard(tender.orderPositions[0]);
    }
    else {
      Core.dialogs.TenderOrdersPopup.hideIfOpen();

      return Core.dialogs.TenderOrdersPopup.showAsPromise({ tender, target });
    }
  }

  /** Карточка позиции заказа тендера — с теми же параметрами, что открывал счётчик заказов */
  static showOrderPositionCard(position: OrderPosition): Promise<boolean> {
    return Core.dialogs.OrderPositionCard.showAsPromise({
      id:          position.id,
      isPreorder:  position.isPreorder,
      isAdminMode: Core.currentUser.isAdmin,
    });
  }

  static showOffersRegistryDialog(
      tender: Tender,
  ): Promise<boolean> {
    if (tender.yourLastBid !== null) {
      Core.showNotification.error('Ставка уже создана');
      return Promise.resolve(false);
    }

    return Core.dialogs.OffersRegistryDialog.showAsPromise(
        {
          title: `Предложения: ${tender.productCategory.name}`,
          params: {onlyMine: true, productCategory: tender.productCategory},
          hiddenColumns: ['isOutdated', 'seller', 'buyer', 'deliveryDays', 'qty', 'price', 'isPublished', 'action'],
          selectable: true,
          tender: tender,
          width: '600px',
        }
    );

  }

  /** По id товара — данные стартовой цены, null у товара — тендеров с заказом по нему не было. Ошибка запроса — null */
  static async getPriceForNewTender(product: Parameters<typeof TendersDAO.getPriceForNewTender>[0], buyerStore: Store, date: Date) {
    const response = await TendersDAO.getPriceForNewTender(product, buyerStore, date);

    if (response.ok) {
      return response.data;
    } else {
      Core.showNotification.error(response.error?.message ?? 'Ошибка получения цен для тендеров');
      return null;
    }
  }

  static async search(pattern: string): Promise<Tender[]> {
    if (typeof pattern !== 'string' || pattern.length === 0) {
      return [];
    }

    const response = await TendersDAO.search(pattern);

    if (!response.ok) {
      Core.showNotification.error('Ошибка получения списка тендеров');
      return [];
    }

    return Utils.isNotEmptyArray(response.data)
        ? response.data.map((item: any) => Tender.fromServer(item))
        : [];
  }

  /**
   * Загрузка списка тендеров с пагинацией.
   * Сигнатура повторяет бэковую — никакой логики табов или внешних фильтров здесь нет.
   * Совместим с DataGridLoader как fetchFunction.
   */
  static async getListPaginated(
      params: Parameters<typeof TendersDAO.getList>[0] & {
        pagination?: RpcDataPagination;
        sort?: RpcDataSort;
      } | undefined,
      options: RequestOptions,
  ): Promise<{ data: Tender[]; pagination: RpcDataPagination | null; meta: RpcMeta | null }> {
    const {pagination, sort, ...filters} = params ?? {};
    const response = await TendersDAO.getList(filters, pagination, sort, options);

    /**
     * pagination возвращается отдельным полем для совместимости с DataGridLoader —
     * loader.loadData() читает result.pagination для обновления пагинации грида.
     * meta возвращается полностью для прямых вызовов: софт-polling использует
     * meta.time.serverTime.end как updatedAfter для следующего запроса.
     */
    return {
      data: Utils.hydrateArray(response.data, Tender.fromServer),
      pagination: response.meta?.pagination ?? null,
      meta: response.meta,
    };
  }

  /**
   * Загрузка списка тендеров.
   * Для использования вне реестра.
   */
  static async getList(
      params?: Parameters<typeof TendersService.getListPaginated>[0]
  ): Promise<Tender[]> {
    return (await this.getListPaginated(params, {})).data;
  }

  static async get(
      id: number | string,
      updatedAfter: Date | null,
      timezone: TimeZone,
      isDeleted: boolean,
      options: RequestOptions,
  ): Promise<{ tender: Tender, lastFetchTimeStamp: Date } | null> {
    const response = await TendersDAO.get(
        id,
        updatedAfter,
        timezone.UTCOffset.split(':')[0],
        isDeleted,
        options,
    );

    if (response.ok) {
      return {
        tender: Tender.fromServer(response.data),
        lastFetchTimeStamp: response.meta.time!.serverTime!.end!
      }
    } else if (response.cancelled) {
      return null;
    } else {
      Core.showNotification.error('Ошибка получения тендера');
      return null;
    }
  }

  static async update(tender: Tender): Promise<boolean> {
    const response = await TendersDAO.update(tender);

    if (response.ok) {
      Core.showNotification.success('Тендер обновлён');
    } else {
      Core.showNotification.error('Не получилось обновить тендер');
    }

    return response.ok;
  }

  static async add(tender: Tender): Promise<boolean> {
    const response = await TendersDAO.add(tender);

    if (response.ok) {
      Core.showNotification.success('Тендер создан');
    } else {
      Core.showNotification.error('Ошибка создания тендера');
    }

    return response.ok;
  }

  static async delete(tenderId: number): Promise<boolean> {
    const response = await TendersDAO.delete(tenderId);

    if (response.ok) {
      Core.showNotification.success('Тендер удалён');
    } else {
      Core.showNotification.error(response.error?.message ?? 'Ошибка удаления тендера');
    }

    return response.ok;
  }

  static async close(tenderId: number): Promise<boolean> {
    const response = await TendersDAO.close(tenderId);

    if (response.ok) {
      Core.showNotification.success('Тендер закрыт');
    } else {
      Core.showNotification.error('Ошибка закрытия тендера');
    }

    return response.ok;
  }

  static async getParticipation(tenderId: number): Promise<ParticipationDetailProduct[]> {
    const response = await TendersDAO.getParticipation(tenderId);

    if (!response.ok) {
      Core.showNotification.error('Ошибка получения участников тендера');
      return [];
    }

    return Utils.isNotEmptyArray(response.data)
        ? response.data.map((_item: any) => ParticipationDetailProduct.fromServer(_item))
        : [];
  }

  static async updateParticipation(participation: ParticipationDetailProduct): Promise<boolean> {
    const response = await TendersDAO.updateParticipation(participation);

    if (response.ok) {
      Core.showNotification.success(`Статус обновлён ${participation.id}`);
    } else {
      Core.showNotification.error('Ошибка обновления статуса участия');
    }

    return response.ok;
  }

  static async toggleCategoryBookmark(tender) {
    const productCategoryId = tender.productCategory.id;
    const result = tender.isBookmarked
        ? await BookmarksService.delete(productCategoryId)
        : await BookmarksService.add(productCategoryId);

    if (result) {
      tender.isBookmarked = !tender.isBookmarked;
    }
  }

  /**
   * Диалог отказа по тендеру. В реестре участия не приходят — подгружаем их
   * при первом обращении. В карточке они уже есть, повторного запроса не будет.
   */
  static async showRefuseDialogByTender(tender: Tender): Promise<boolean> {
    if (tender.participants === null) {
      await tender.loadParticipants();
    }

    return await TendersService.showRefuseDialog(tender.participationDetailProductsNotExcludedByCurrentPartner);
  }

  /**
   * Диалог отказа. Принимает только участия по разрешениям, допущенным к
   * торгам: по исключённому разрешению отказываться не от чего, и диалог
   * проставит отказ во всё, что ему передали.
   */
  static showRefuseDialog(
      participationDetailProducts: ParticipationDetailProduct[],
  ): Promise<boolean> {
    return Core.dialogs.TenderRefuseDialog.showAsPromise({
      participationDetailProducts
    })
  }


  /** Метод возвращает иконку в зависимости от состояния тендера */
  static getIsBookmarkedIcon(tender) {
    return tender.isBookmarked ? 'star' : 'star_border';
  }

  static getIsBookmarkedText(tender) {
    return tender.isBookmarked ? 'Убрать из избранного' : 'Добавить в избранное';
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Контекстное меню тендера для грида реестра и карточки.
   *
   * Геттер (не статическое поле), потому что:
   *  - доступность некоторых пунктов зависит от Core.currentPartner
   *    (роль покупатель/поставщик), а Core.currentPartner может быть
   *    не определён на этапе загрузки модуля
   *  - часть пунктов вообще скрывается в зависимости от роли (hidden-функция)
   *
   * Callback возвращает true, только если тендер изменился: грид тогда
   * обновляет список, карточка — перечитывает тендер (или закрывается, если
   * его удалили).
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
          'Открыть',
          'open_in_new',
          (tender: Tender) => { return TendersService.showTenderCard(tender); },
          null,
          false,  // hidden
          true,   // dividerAfter
      ),

      new ContextMenuItem(
          'Сравнить предложения',
          'emoji_events',
          (tender: Tender) => { return TendersService.compareWithDemand(tender); },
          (tender: Tender) => { return tender.getReasonCannotCompareWithDemandByCurrentUser(); },
          () => { return Core.currentPartner.isBuyer === false; },
      ),

      new ContextMenuItem(
          'Открыть заказ',
          'open_in_new',
          (tender: Tender) => { return TendersService.showOrders(tender); },
          (tender: Tender) => { return tender.getReasonCannotShowOrderByCurrentUser(); },
      ),

      new ContextMenuItem(
          (tender) => { return tender.yourLastBid !== null ? 'Предложить цену ниже' : 'Сделать ставку'; },
          'add_box',
          (tender: Tender) => {
            if (tender.yourLastBid !== null) {
              return TendersService.showChangeBidPopup(tender);
            } else {
              return TendersService.showOffersRegistryDialog(tender);
            }
          },
          (tender: Tender) => { return tender.getReasonCannotCreateBidByCurrentUser(); },
          Core.currentPartner.isBuyer,
      ),

      new ContextMenuItem(
          'Удалить ставку',
          'delete',
          (tender: Tender) => { return TendersService.deleteOwnBid(tender); },
          (tender: Tender) => { return tender.getReasonCannotDeleteOwnBidByCurrentUser(); },
          Core.currentPartner.isBuyer,
      ),

      new ContextMenuItem(
          'Отправить заявку на участие',
          'send',
          (tender: Tender) => { return TendersService.sendParticipationRequest(tender); },
          (tender: Tender) => { return tender.getReasonCannotSendParticipationRequest(); },
          Core.currentPartner.isBuyer,
      ),

      new ContextMenuItem(
          'Согласиться на участие',
          'check_circle',
          (tender: Tender) => { return TendersService.agreeParticipate(tender); },
          (tender: Tender) => { return tender.getReasonCannotAgreeParticipateByCurrentUser(); },
          (tender: Tender) => { return Core.currentPartner.isBuyer; },
      ),

      new ContextMenuItem(
          'Изменить причину неучастия',
          'compare_arrows',
          (tender: Tender) => { return TendersService.showRefuseDialogByTender(tender); },
          (tender: Tender) => { return tender.getReasonCannotChangeRefusalReasonsByCurrentUser(); },
          (tender: Tender) => { return Core.currentPartner.isBuyer; },
      ),

      new ContextMenuItem(
          'Отказаться от участия',
          'cancel',
          (tender: Tender) => { return TendersService.showRefuseDialogByTender(tender); },
          (tender: Tender) => { return tender.getReasonCannotSetRefusalReasonsByCurrentUser(); },
          (tender: Tender) => { return Core.currentPartner.isBuyer || tender.isRefusedTenderByCurrentPartner === true; },
          true,   // dividerAfter
      ),

      new ContextMenuItem(
          'Открыть потребность',
          'open_in_new',
          (tender: Tender) => {
            return DemandsService.showDemandCard(tender.demand);
          },
      ),

      new ContextMenuItem(
          'Закрыть тендер',
          'cancel',
          (tender: Tender) => { return TendersService.closeTenderWithConfirmation(tender); },
          (tender: Tender) => { return tender.getReasonCannotCancelByCurrentUser(); },
          () => { return Core.currentPartner.isBuyer === false; },
      ),

      new ContextMenuItem(
          'Добавить/Убрать из избранного',
          'star_border',
          (tender: Tender) => {
            return TendersService.toggleCategoryBookmark(tender);
          },
      ),

      // В самой карточке тендера есть фид
      // new ContextMenuItem(
      //   'История',
      //   'history',
      //   (tender: Tender) => {
      //     Core.dialogs.FeedDialog.show({
      //       entityTypeName: 'tender',
      //       entityId:       tender.id,
      //       showAddComment: false,
      //     });
      //   },
      //   null,
      //   false,  // hidden
      //   true,   // dividerAfter
      // ),

      new ContextMenuItem(
          'Удалить',
          'delete',
          (tender: Tender) => {
            return TendersService.deleteWithConfirmation(tender);
          },
          (tender: Tender) => { return tender.getReasonCannotDeleteTenderByCurrentUser(); },
          () => { return Core.currentPartner.isBuyer === false; },
      ),
    ];
  }

  /**
   * Удаление собственной ставки с подтверждением.
   * Возвращает true, если ставка удалена.
   */
  static async deleteOwnBid(tender: Tender): Promise<boolean> {
    const isConfirmed = await Core.confirm(null, 'Подтверждаете удаление?');

    if (isConfirmed === true) {
      return await TenderBidsService.deleteBid(tender.currentPartnerBid.id);
    } else {
      return false;
    }
  }

  /**
   * Согласие на участие — снимает отказ, обнуляя participation.
   * Участий у поставщика может быть несколько, по одному на разрешение:
   * Исключённые из торгов разрешения не трогаем — играть по ним всё равно нельзя.
   *
   * В реестре участия не приходят — подгружаем их при первом обращении.
   * Возвращает true, если обновилось хотя бы одно участие.
   */
  static async agreeParticipate(tender: Tender): Promise<boolean> {
    if (tender.participants === null) {
      await tender.loadParticipants();
    }

    let isUpdated = false;

    for (const participationDetailProduct of tender.participationDetailProductsNotExcludedByCurrentPartner) {
      const participation = Utils.cloneDeep(participationDetailProduct);

      participation.setParticipation(null);

      if (await TendersService.updateParticipation(participation) === true) {
        isUpdated = true;
      }
    }

    return isUpdated;
  }

  /**
   * Отправляет заявку на участие в тендере с подтверждением.
   * Возвращает true, если заявка отправлена.
   */
  static async sendParticipationRequest(tender: Tender): Promise<boolean> {
    const isConfirmed = await Core.confirm('Отправить заявку на участие в тендере?');

    if (isConfirmed === true) {
      const request = TenderParticipationRequest.fromTender(tender);
      return await TenderParticipationRequestsService.send(request);
    } else {
      return false;
    }
  }

  /**
   * Сравнение тендера с потребностью — открывает диалог OrderOptionsComparisonDialog.
   * Промис резолвится по закрытию диалога: true — что-то сделали, false — нет.
   */
  static compareWithDemand(tender: Tender): Promise<boolean> {
    return Core.dialogs.OrderOptionsComparisonDialog.showAsPromise({ tenderId: tender.id } );
  }

  /**
   * Закрытие тендера с подтверждением.
   * Возвращает true, если тендер закрыт.
   */
  static async closeTenderWithConfirmation(tender: Tender): Promise<boolean> {
    const isConfirmed = await Core.confirm(
        'Тендер будет перенесён в раздел "Создан заказ" и кол-во в тендере уменьшится до заказанного',
        'Закрыть тендер?',
    );

    if (isConfirmed === true) {
      return await TendersService.close(tender.id);
    } else {
      return false;
    }
  }

  /**
   * Удаление тендера с подтверждением.
   * При успехе помечает переданный тендер удалённым — по этому признаку карточка
   * понимает, что после пункта меню её надо закрыть, а не перечитывать.
   */
  static async deleteWithConfirmation(tender: Tender): Promise<boolean> {
    const isConfirmed = await Core.confirm('Удалить тендер?');

    if (isConfirmed === true) {
      const isDeleted = await TendersService.delete(tender.id);

      if (isDeleted === true) {
        tender.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  /**
   * Открывает попап изменения ставки. Если у поставщика уже есть ставка —
   * один путь, если нет — догружает тендер с характеристиками и открывает
   * диалог списка предложений.
   */
  static async showChangeBidPopup(
      tender: Tender,
      target: HTMLElement | null = null,
  ): Promise<boolean> {
    Core.dialogs.TenderChangeBidPopup.hideIfOpen();

    // Догружаем тендер целиком, чтобы получить product.characteristics
    // (в реестре они не приходят, чтобы не тащить лишнее).
    const result = await TendersService.get(
        tender.id,
        null,
        Core.timeZone,
        false,
        {},
    );

    if (Core.utils.isNullOrUndefined(result) === true) {
      // запрос отменён либо бэк ответил ошибкой (уведомление уже показал get) — попап не открываем
      return false;
    }
    else {
      return await Core.dialogs.TenderChangeBidPopup.showAsPromise({
        bid: result.tender.yourLastBid,
        bestBid: result.tender.bestBid,
        target,
      });
    }
  }

  /** Завершённые тендеры текущего покупателя по номенклатуре для графика истории цен */
  static async getPriceHistory(productId: number): Promise<TenderPricePoint[]> {
    const response = await TendersDAO.getPriceHistory(productId);

    return Core.utils.hydrateArray(response.data, TenderPricePoint.fromServer);
  }
}

export default TendersService;
